// Window-level mouse tracking for a drag/resize that is ALREADY under way.
//
// The listeners attach synchronously, in the same task as the event that
// started the drag. The editor used to attach them from a React effect that
// ran after the drag-start state committed. A quick press → move → release
// could deliver its mouseup before that commit, so the release was never
// seen: the item stayed "grabbed" and followed the bare pointer until the
// next click, flinging it hundreds of pixels away.
//
// As a second guard, a mousemove with the primary button no longer held
// ends the drag instead of moving the item. That covers a release this
// window never saw, e.g. one outside the window.
//
// Returns { end(reason), stop() }. end() removes the listeners and reports
// onEnd(reason). stop() only removes them, for an owner that is already
// tearing the drag down itself. Both are idempotent.
export function trackPointerDrag({ onMove, onEnd, target = window }) {
  let active = true;

  const handleMove = (e) => {
    if (!active) return;
    if (typeof e.buttons === 'number' && (e.buttons & 1) === 0) {
      end('released');
      return;
    }
    onMove(e);
  };
  const handleUp = (e) => end('drop', e);
  const handleBlur = () => end('blur');

  function detach() {
    target.removeEventListener('mousemove', handleMove);
    target.removeEventListener('mouseup', handleUp);
    target.removeEventListener('blur', handleBlur);
  }

  function end(reason, event) {
    if (!active) return;
    active = false;
    detach();
    onEnd?.(reason, event);
  }

  function stop() {
    if (!active) return;
    active = false;
    detach();
  }

  target.addEventListener('mousemove', handleMove);
  target.addEventListener('mouseup', handleUp);
  target.addEventListener('blur', handleBlur);

  return { end, stop };
}
